import {
  buildMemberNameMap,
  getMemberLabel,
  getMemberValueKeys,
  getPrimaryMemberValue,
  memberDirectoryStore,
  mergeOptions as mergeMemberOptions,
  toMemberOptions,
  toUniqueOptions,
} from '@/modules/zy-crm/member-directory';
import { queryAreas } from '@/services/bff/area.api';
import { queryContacts } from '@/services/bff/contact.api';
import { queryCrmResource } from '@/services/bff/crm.api';
import { queryCustomers } from '@/services/bff/customer.api';
import {
  queryOptionItemOptions,
  querySalesPipelineOptions,
} from '@/services/bff/dictionary-management.api';
import { queryIndustries } from '@/services/bff/industry.api';
import { queryLeads } from '@/services/bff/lead.api';
import { resolveRelationText } from './display';

export type RelationOption = {
  label: string;
  value: string;
  description?: string;
  raw?: Record<string, unknown>;
};

const normalize = (value: unknown) => String(value ?? '').trim();

export {
  buildMemberNameMap,
  getMemberLabel,
  getMemberValueKeys,
  getPrimaryMemberValue,
  toMemberOptions,
  toUniqueOptions,
};

export const mergeOptions = (
  prev: RelationOption[],
  next: RelationOption[],
  ensureValue?: string,
): RelationOption[] => mergeMemberOptions(prev, next, ensureValue);

const pickLabel = (record: Record<string, unknown>, fallback: string) => {
  const label =
    normalize(record.name) ||
    normalize(record.nickname) ||
    normalize(record.username) ||
    normalize(record.title) ||
    normalize(record.phone) ||
    fallback;
  return label || fallback;
};

const toOption = (
  record: Record<string, unknown>,
  valueCandidates: string[],
): RelationOption | null => {
  for (const key of valueCandidates) {
    const value = normalize(record[key]);
    if (value === '') continue;
    return {
      value,
      label: pickLabel(record, value),
      raw: record,
    };
  }
  return null;
};

export const resolveRelationLabel = (
  value: unknown,
  options: RelationOption[],
  fallback?: string,
) => {
  return resolveRelationText({
    value,
    options,
    fallbackName: fallback,
  });
};

export async function fetchMemberOptions(keyword?: string) {
  return memberDirectoryStore.searchMembers(keyword);
}

export async function fetchCustomerOptions(keyword?: string) {
  const page = await queryCustomers({
    page: 1,
    pageSize: 100,
    keyword: keyword || undefined,
  });
  return toUniqueOptions(
    (page?.list || [])
      .map((item) =>
        toOption(item as unknown as Record<string, unknown>, ['uuid']),
      )
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchLeadOptions(keyword?: string) {
  const page = await queryLeads({
    page: 1,
    pageSize: 100,
    keyword: keyword || undefined,
  });
  return toUniqueOptions(
    (page?.list || [])
      .map((item) =>
        toOption(item as unknown as Record<string, unknown>, ['uuid']),
      )
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchContactOptions(
  keyword?: string,
  customerUuid?: string,
) {
  const params = {
    pageSize: 100,
    keyword: keyword?.trim() || undefined,
    customerUuid: customerUuid || undefined,
  };
  const firstPage = await queryContacts({ ...params, page: 1 });
  const contacts = [...(firstPage.list || [])];
  // 关联客户前还需筛选归属，必须读完匹配结果，避免遗漏后续页的可选联系人。
  const pageSize = firstPage.pageSize || params.pageSize;
  const pageCount = Math.ceil(firstPage.total / pageSize);
  for (let page = 2; page <= pageCount; page += 1) {
    const result = await queryContacts({ ...params, page });
    contacts.push(...result.list);
  }
  return toUniqueOptions(
    contacts
      .map((item) =>
        toOption(item as unknown as Record<string, unknown>, ['uuid']),
      )
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchOpportunityOptions(keyword?: string) {
  const page = await queryCrmResource<Record<string, unknown>>(
    'opportunities',
    {
      page: 1,
      pageSize: 100,
      ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
    } as any,
  );
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchContractOptions(
  keyword?: string,
  customerUuid?: string,
  opportunityUuid?: string,
) {
  const page = await queryCrmResource<Record<string, unknown>>('contracts', {
    page: 1,
    pageSize: 100,
    ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
    ...(customerUuid ? ({ customerUuid } as Record<string, unknown>) : {}),
    ...(opportunityUuid
      ? ({ opportunityUuid } as Record<string, unknown>)
      : {}),
  } as any);
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchQuoteOptions(
  keyword?: string,
  customerUuid?: string,
  opportunityUuid?: string,
) {
  const page = await queryCrmResource<Record<string, unknown>>('quotes', {
    page: 1,
    pageSize: 100,
    ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
    ...(customerUuid ? ({ customerUuid } as Record<string, unknown>) : {}),
    ...(opportunityUuid
      ? ({ opportunityUuid } as Record<string, unknown>)
      : {}),
  } as any);
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchInvoiceOptions(keyword?: string) {
  const page = await queryCrmResource<Record<string, unknown>>('invoices', {
    page: 1,
    pageSize: 100,
    ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
  } as any);
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchReceiptOptions(keyword?: string) {
  const page = await queryCrmResource<Record<string, unknown>>('receipts', {
    page: 1,
    pageSize: 100,
    ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
  } as any);
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchPipelineOptions(keyword?: string) {
  const page = await queryCrmResource<Record<string, unknown>>(
    'opportunity-pipelines',
    {
      page: 1,
      pageSize: 200,
      ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
    } as any,
  );
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchEnabledPipelineOptions(keyword?: string) {
  const list = await querySalesPipelineOptions(keyword);
  return toUniqueOptions(
    (list || [])
      .map((item) => {
        const record = item as unknown as Record<string, unknown>;
        const value = normalize(item?.value || item?.uuid);
        if (!value) return null;
        return {
          value,
          label:
            normalize(item?.label) ||
            normalize(record.name) ||
            normalize(record.title) ||
            normalize(item?.code) ||
            value,
          raw: record,
        };
      })
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchStageOptions(
  keyword?: string,
  pipelineUuid?: string,
) {
  const page = await queryCrmResource<Record<string, unknown>>(
    'opportunity-stages',
    {
      page: 1,
      pageSize: 200,
      ...(keyword ? ({ keyword } as Record<string, unknown>) : {}),
      ...(pipelineUuid ? ({ pipelineUuid } as Record<string, unknown>) : {}),
    } as any,
  );
  return toUniqueOptions(
    (page?.list || [])
      .map((item) => toOption(item, ['uuid']))
      .filter(Boolean) as RelationOption[],
  );
}

export async function fetchAreaOptions(keyword?: string) {
  const page = await queryAreas({ page: 1, pageSize: 200 });
  const options = toUniqueOptions(
    (page?.list || [])
      .map((item) =>
        toOption(item as unknown as Record<string, unknown>, ['uuid']),
      )
      .filter(Boolean) as RelationOption[],
  );
  if ((keyword || '').trim() === '') return options;
  const kw = String(keyword).trim().toLowerCase();
  return options.filter(
    (item) =>
      item.label.toLowerCase().includes(kw) ||
      item.value.toLowerCase().includes(kw),
  );
}

export async function fetchIndustryOptions(keyword?: string) {
  const page = await queryIndustries({ page: 1, pageSize: 200 });
  const options = toUniqueOptions(
    (page?.list || [])
      .map((item) =>
        toOption(item as unknown as Record<string, unknown>, ['uuid']),
      )
      .filter(Boolean) as RelationOption[],
  );
  if ((keyword || '').trim() === '') return options;
  const kw = String(keyword).trim().toLowerCase();
  return options.filter(
    (item) =>
      item.label.toLowerCase().includes(kw) ||
      item.value.toLowerCase().includes(kw),
  );
}

export async function fetchSourceChannelOptions(keyword?: string) {
  const list = await queryOptionItemOptions('crm.customer_source', keyword);
  const options = toUniqueOptions(
    (list || [])
      .map((item) => {
        const value = normalize(item.value || item.uuid);
        const label = normalize(item.label);
        if (!value || !label) {
          return null;
        }
        return {
          value,
          label,
          description: normalize(item.description) || undefined,
          raw: item as unknown as Record<string, unknown>,
        };
      })
      .filter(Boolean) as RelationOption[],
  );
  if ((keyword || '').trim() === '') return options;
  const kw = String(keyword).trim().toLowerCase();
  return options.filter(
    (item) =>
      item.label.toLowerCase().includes(kw) ||
      item.value.toLowerCase().includes(kw),
  );
}

export async function fetchCustomerLevelOptions(keyword?: string) {
  const list = await queryOptionItemOptions('crm.customer_level', keyword);
  const options = toUniqueOptions(
    (list || [])
      .map((item) => {
        const value = normalize(item.value || item.uuid);
        const label = normalize(item.label);
        if (!value || !label) {
          return null;
        }
        return { value, label };
      })
      .filter(Boolean) as RelationOption[],
  );
  if ((keyword || '').trim() === '') return options;
  const kw = String(keyword).trim().toLowerCase();
  return options.filter(
    (item) =>
      item.label.toLowerCase().includes(kw) ||
      item.value.toLowerCase().includes(kw),
  );
}
